import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { TradingJournal } from '../components/ui/TradingJournal';
import { RiskCalculator } from '../components/ui/RiskCalculator';
import { TradingPlan } from '../components/ui/TradingPlan';
import { toast } from 'sonner';
import {
    FileText,
    FileSpreadsheet,
    Download,
    Calculator,
    BookOpen,
    NotebookPen,
    FolderOpen,
    Star,
    Zap,
    FileCheck,
    Eye,
    X,
    Maximize2,
    ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryInfo = {
    pdf_notes: { 
        icon: FileText, 
        color: 'bg-red-50 text-red-600',
        gradient: 'from-red-500 to-red-600',
        label_id: 'Nota PDF',
        label_en: 'PDF Notes'
    },
    trading_plan: { 
        icon: BookOpen, 
        color: 'bg-blue-50 text-blue-600',
        gradient: 'from-blue-500 to-blue-600',
        label_id: 'Trading Plan',
        label_en: 'Trading Plan'
    },
    risk_calculator: { 
        icon: Calculator, 
        color: 'bg-emerald-50 text-emerald-600',
        gradient: 'from-emerald-500 to-emerald-600',
        label_id: 'Kalkulator Risiko',
        label_en: 'Risk Calculator'
    },
    trading_journal: { 
        icon: NotebookPen, 
        color: 'bg-purple-50 text-purple-600',
        gradient: 'from-purple-500 to-purple-600',
        label_id: 'Trading Journal',
        label_en: 'Trading Journal'
    }
};

const fileTypeIcons = {
    pdf: FileText,
    xlsx: FileSpreadsheet,
    xls: FileSpreadsheet,
    doc: FileText,
    docx: FileText
};

export const ResourceVaultPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { t, language, getLocalizedField } = useLanguage();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfViewer, setPdfViewer] = useState({ open: false, resource: null });

    const currentCategory = searchParams.get('category') || 'all';

    useEffect(() => {
        fetchResources();
    }, [currentCategory]);

    const fetchResources = async () => {
        setLoading(true);
        try {
            const url = currentCategory === 'all' || currentCategory === 'trading_journal'
                ? `${API}/resources`
                : `${API}/resources?category=${currentCategory}`;
            const response = await axios.get(url);
            // Sort resources: items with actual file URLs first, placeholder (#) URLs last
            const sorted = response.data.sort((a, b) => {
                const aHasUrl = a.file_url && a.file_url !== '#';
                const bHasUrl = b.file_url && b.file_url !== '#';
                if (aHasUrl && !bHasUrl) return -1;
                if (!aHasUrl && bHasUrl) return 1;
                return 0;
            });
            setResources(sorted);
        } catch (error) {
            console.error('Error fetching resources:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryChange = (category) => {
        if (category === 'all') {
            searchParams.delete('category');
        } else {
            searchParams.set('category', category);
        }
        setSearchParams(searchParams);
    };

    const handleDownload = async (resource) => {
        try {
            await axios.post(`${API}/resources/${resource.id}/download`);
            
            if (resource.file_url && resource.file_url !== '#') {
                window.open(resource.file_url, '_blank');
            } else {
                toast.info(
                    language === 'id' 
                        ? 'File belum tersedia untuk didownload'
                        : 'File not available for download yet'
                );
            }
        } catch (error) {
            console.error('Error tracking download:', error);
        }
    };

    const handleViewPdf = (resource) => {
        if (resource.file_type === 'pdf' && resource.file_url && resource.file_url !== '#') {
            setPdfViewer({ open: true, resource });
        } else {
            toast.info(
                language === 'id' 
                    ? 'Preview hanya tersedia untuk file PDF'
                    : 'Preview only available for PDF files'
            );
        }
    };

    const closePdfViewer = () => {
        setPdfViewer({ open: false, resource: null });
    };

    if (loading && currentCategory !== 'trading_journal' && currentCategory !== 'risk_calculator' && currentCategory !== 'trading_plan') {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-12 w-full" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} className="h-40" />
                        ))}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* PDF Viewer Dialog */}
                <Dialog open={pdfViewer.open} onOpenChange={(open) => !open && closePdfViewer()}>
                    <DialogContent className="max-w-6xl w-[95vw] h-[90vh] p-0 bg-slate-900 border-slate-700">
                        <DialogHeader className="p-4 border-b border-slate-700 flex flex-row items-center justify-between">
                            <DialogTitle className="text-white font-heading">
                                {pdfViewer.resource && getLocalizedField(pdfViewer.resource, 'title')}
                            </DialogTitle>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => window.open(pdfViewer.resource?.file_url, '_blank')}
                                    className="bg-slate-800 border-slate-600 text-white hover:bg-slate-700"
                                    data-testid="pdf-fullscreen"
                                >
                                    <Maximize2 className="w-4 h-4 mr-2" />
                                    {language === 'id' ? 'Buka Tab Baru' : 'Open New Tab'}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDownload(pdfViewer.resource)}
                                    className="bg-slate-800 border-slate-600 text-white hover:bg-slate-700"
                                    data-testid="pdf-download"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download
                                </Button>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={closePdfViewer}
                                    className="text-slate-400 hover:text-white"
                                    data-testid="pdf-close"
                                >
                                    <X className="w-5 h-5" />
                                </Button>
                            </div>
                        </DialogHeader>
                        <div className="flex-1 h-[calc(90vh-80px)]">
                            {pdfViewer.resource && (
                                <iframe
                                    src={`${pdfViewer.resource.file_url}#toolbar=1&navpanes=1&scrollbar=1`}
                                    className="w-full h-full border-0"
                                    title={getLocalizedField(pdfViewer.resource, 'title')}
                                    data-testid="pdf-iframe"
                                />
                            )}
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Header */}
                <div>
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
                        {t('resourceVault')}
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {language === 'id' 
                            ? 'Download template dan resource untuk trading'
                            : 'Download templates and trading resources'
                        }
                    </p>
                </div>

                {/* Category Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {/* All Resources */}
                    <button
                        onClick={() => handleCategoryChange('all')}
                        className={`glass-card rounded-xl p-4 text-left transition-all duration-300 ${
                            currentCategory === 'all' 
                                ? 'ring-2 ring-primary/30 shadow-lg' 
                                : 'hover:shadow-md'
                        }`}
                        data-testid="category-all"
                    >
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-md mb-3">
                            <FolderOpen className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="font-semibold text-slate-900 text-sm">
                            {language === 'id' ? 'Semua' : 'All'}
                        </h3>
                    </button>

                    {/* Category Cards */}
                    {Object.entries(categoryInfo).map(([key, info]) => {
                        const Icon = info.icon;
                        const isActive = currentCategory === key;
                        const isJournal = key === 'trading_journal';
                        const isCalculator = key === 'risk_calculator';
                        const isPlan = key === 'trading_plan';
                        
                        return (
                            <button
                                key={key}
                                onClick={() => handleCategoryChange(key)}
                                className={`glass-card rounded-xl p-4 text-left transition-all duration-300 relative ${
                                    isActive 
                                        ? 'ring-2 ring-primary/30 shadow-lg' 
                                        : 'hover:shadow-md'
                                }`}
                                data-testid={`category-${key}`}
                            >
                                {isJournal && (
                                    <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                                        <Star className="w-3 h-3" fill="currentColor" />
                                        NEW
                                    </span>
                                )}
                                {isCalculator && (
                                    <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                                        <Zap className="w-3 h-3" fill="currentColor" />
                                        LIVE
                                    </span>
                                )}
                                {isPlan && (
                                    <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                                        <FileCheck className="w-3 h-3" />
                                        TEMPLATE
                                    </span>
                                )}
                                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${info.gradient} flex items-center justify-center shadow-md mb-3`}>
                                    <Icon className="w-5 h-5 text-white" />
                                </div>
                                <h3 className="font-semibold text-slate-900 text-sm">
                                    {language === 'id' ? info.label_id : info.label_en}
                                </h3>
                            </button>
                        );
                    })}
                </div>

                {/* Trading Plan (Interactive) */}
                {currentCategory === 'trading_plan' && (
                    <div className="glass-card rounded-2xl p-6 md:p-8">
                        <div className="mb-6">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full mb-3">
                                <BookOpen className="w-4 h-4 text-blue-600" />
                                <span className="text-xs font-medium text-blue-600">
                                    {language === 'id' ? 'Template Interaktif' : 'Interactive Template'}
                                </span>
                            </div>
                            <h2 className="text-xl font-heading font-bold text-slate-900">
                                Trading Plan
                            </h2>
                            <p className="text-slate-500 text-sm mt-1">
                                {language === 'id' 
                                    ? 'Isi dan cetak sebagai panduan trading harian anda'
                                    : 'Fill and print as your daily trading guide'
                                }
                            </p>
                        </div>
                        <TradingPlan />
                    </div>
                )}

                {/* Risk Calculator (Interactive) */}
                {currentCategory === 'risk_calculator' && (
                    <div className="glass-card rounded-2xl p-6 md:p-8">
                        <RiskCalculator />
                    </div>
                )}

                {/* Trading Journal (Interactive) */}
                {currentCategory === 'trading_journal' && (
                    <div className="glass-card rounded-2xl p-6 md:p-8">
                        <div className="mb-6">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-full mb-3">
                                <NotebookPen className="w-4 h-4 text-purple-600" />
                                <span className="text-xs font-medium text-purple-600">
                                    {language === 'id' ? 'Template Interaktif' : 'Interactive Template'}
                                </span>
                            </div>
                            <h2 className="text-xl font-heading font-bold text-slate-900">
                                {language === 'id' ? 'Trading Journal Harian' : 'Daily Trading Journal'}
                            </h2>
                            <p className="text-slate-500 text-sm mt-1">
                                {language === 'id' 
                                    ? 'Isi journal ini selepas setiap trade. Boleh cetak sebagai PDF.'
                                    : 'Fill this journal after every trade. Can be printed as PDF.'
                                }
                            </p>
                        </div>
                        <TradingJournal />
                    </div>
                )}

                {/* Resources Grid (for other categories) */}
                {currentCategory !== 'trading_journal' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {resources.length === 0 ? (
                            <div className="col-span-2 glass-card rounded-2xl text-center py-16">
                                <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                                <h3 className="font-semibold text-slate-700 mb-2">
                                    {language === 'id' 
                                        ? 'Belum Ada Resource'
                                        : 'No Resources Yet'
                                    }
                                </h3>
                                <p className="text-slate-500 text-sm">
                                    {language === 'id' 
                                        ? 'Admin akan menambah resource di sini'
                                        : 'Admin will add resources here'
                                    }
                                </p>
                            </div>
                        ) : (
                            resources.map((resource) => {
                                const info = categoryInfo[resource.category];
                                const Icon = info?.icon || FileText;
                                const FileIcon = fileTypeIcons[resource.file_type] || FileText;

                                return (
                                    <Card
                                        key={resource.id}
                                        className="glass-card border-0 overflow-hidden hover:shadow-lg transition-all duration-300"
                                        data-testid={`resource-card-${resource.id}`}
                                    >
                                        <CardContent className="pt-6">
                                            <div className="flex items-start gap-4">
                                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${info?.gradient || 'from-slate-500 to-slate-600'} shadow-md`}>
                                                    <Icon className="w-6 h-6 text-white" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-heading font-semibold text-slate-900">
                                                        {getLocalizedField(resource, 'title')}
                                                    </h3>
                                                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                                                        {getLocalizedField(resource, 'description')}
                                                    </p>
                                                    <div className="flex items-center gap-4 mt-3">
                                                        <span className="text-xs text-slate-400 flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-full">
                                                            <FileIcon className="w-3.5 h-3.5" />
                                                            {resource.file_type.toUpperCase()}
                                                        </span>
                                                        <span className="text-xs text-slate-400">
                                                            {resource.download_count} {t('downloads')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex gap-2 mt-4">
                                                {resource.file_type === 'pdf' && resource.file_url && resource.file_url !== '#' && (
                                                    <Button
                                                        className="flex-1 rounded-xl bg-primary hover:bg-primary/90"
                                                        onClick={() => handleViewPdf(resource)}
                                                        data-testid={`view-${resource.id}`}
                                                    >
                                                        <Eye className="w-4 h-4 mr-2" />
                                                        {language === 'id' ? 'Baca' : 'Read'}
                                                    </Button>
                                                )}
                                                <Button
                                                    className={`${resource.file_type === 'pdf' && resource.file_url && resource.file_url !== '#' ? 'flex-1' : 'w-full'} rounded-xl`}
                                                    variant="outline"
                                                    onClick={() => handleDownload(resource)}
                                                    data-testid={`download-${resource.id}`}
                                                >
                                                    <Download className="w-4 h-4 mr-2" />
                                                    {t('download')}
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};
